// AuthGate and authenticated app shell (Tasks 13.1, 14.1).
//
// Holds the Google Sign-In session and routes:
//  - unauthenticated visitors to the sign-in screen (Req 1.1, 1.2, 1.4),
//  - role-less users to Role Selection (Req 1.6, 2.1),
//  - unapproved Restaurant/NGO users to an awaiting-verification confirmation
//    (Req 2.7, 5.1), and
//  - approved users to their role dashboard.
//
// Session resolution and every privileged action go through authenticated
// callables (api layer); the client never bypasses the server-authoritative
// backend.

import { useEffect, useState } from "react";
import type { User as FirebaseUser } from "firebase/auth";
import type { Role } from "@sharebite/domain";
import { Skeleton } from "../../components/UIStateWrapper";
import { Card } from "../../components/primitives";
import { useAsyncData } from "../../hooks/useAsyncData";
import * as api from "../../lib/api";
import { DonationEditor } from "../donation/DonationEditor";
import { RestaurantDashboard } from "../dashboard/RestaurantDashboard";
import { NGODashboard } from "../dashboard/NGODashboard";
import { AdminPanel } from "../admin/AdminPanel";
import { NotificationCenter } from "../notifications/NotificationCenter";
import { ProfileView } from "../profile/ProfileView";
import { RoleSelection, type SelectableRole } from "./RoleSelection";
import { RestaurantRegistrationForm } from "./RestaurantRegistrationForm";
import { NGORegistrationForm } from "./NGORegistrationForm";

export function AuthGate(): JSX.Element {
  const [user, setUser] = useState<FirebaseUser | null | undefined>(undefined);

  useEffect(() => api.onAuthChange((u) => setUser(u)), []);

  if (user === undefined) {
    return (
      <div className="sb-main">
        <Skeleton rows={4} />
      </div>
    );
  }

  if (user === null) {
    return <SignIn />;
  }

  return <AuthedApp key={user.uid} user={user} />;
}

function SignIn(): JSX.Element {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function signIn(): Promise<void> {
    setError(null);
    setBusy(true);
    try {
      await api.signInWithGoogle();
    } catch {
      // Cancelled, failed, or timed-out Google Sign-In (Req 1.4).
      setError("Sign-in did not succeed. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="sb-hero">
      <div className="sb-hero__card">
        <p className="sb-hero__eyebrow">Food rescue network</p>
        <h1 className="sb-hero__title">Surplus food, shared with purpose.</h1>
        <p className="sb-hero__lead">
          ShareBite connects verified restaurants with trusted NGOs so good food
          reaches people instead of landfills. Sign in to publish surplus
          donations or claim what your community needs.
        </p>
        <button
          type="button"
          className="sb-btn"
          onClick={() => void signIn()}
          disabled={busy}
        >
          {busy ? "Signing in…" : "Sign in with Google"}
        </button>
        {error && (
          <p className="sb-field__error" role="alert" style={{ marginTop: 12 }}>
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

function AuthedApp({ user }: { user: FirebaseUser }): JSX.Element {
  const { state, reload } = useAsyncData(() => api.resolveSession());

  if (state.status === "loading") {
    return (
      <div className="sb-main">
        <Skeleton rows={4} />
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="sb-main">
        <div className="sb-error" role="alert">
          <p>Couldn't load your account. Please try again.</p>
          <button type="button" className="sb-btn sb-btn--secondary" onClick={reload}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  const { session, route } = state.data;

  if (route.destination === "role-selection") {
    return <Onboarding uid={session.uid} onComplete={reload} />;
  }

  if (route.destination === "awaiting-verification") {
    return <AwaitingVerification />;
  }

  return (
    <RoleHome
      role={route.role}
      session={session}
      displayName={user.displayName ?? user.email ?? "My Profile"}
    />
  );
}

function Onboarding({
  uid,
  onComplete,
}: {
  uid: string;
  onComplete: () => void;
}): JSX.Element {
  const [selected, setSelected] = useState<SelectableRole | null>(null);

  if (selected === null) {
    return <RoleSelection onSelect={setSelected} />;
  }
  if (selected === "Restaurant") {
    return <RestaurantRegistrationForm uid={uid} onSubmitted={onComplete} />;
  }
  return <NGORegistrationForm uid={uid} onSubmitted={onComplete} />;
}

function AwaitingVerification(): JSX.Element {
  return (
    <div className="sb-main">
      <Card title="Application submitted">
        <p>
          Your application has been submitted for review. You'll be notified
          once an administrator approves your account.
        </p>
        <button
          type="button"
          className="sb-btn sb-btn--secondary"
          onClick={() => void api.signOutUser()}
        >
          Sign out
        </button>
      </Card>
    </div>
  );
}

type View = "home" | "create" | "notifications" | "profile";

function RoleHome({
  role,
  session,
  displayName,
}: {
  role: Role;
  session: { uid: string; role: Role | null; verificationStatus: import("@sharebite/domain").VerificationStatus };
  displayName: string;
}): JSX.Element {
  const [view, setView] = useState<View>("home");
  const isProfileRole = role === "Restaurant" || role === "NGO";

  return (
    <div>
      <header className="sb-appbar">
        <div className="sb-appbar__inner">
          <span className="sb-brand">
            <img className="sb-logo" src="/sharebite-logo.png" alt="ShareBite" />
          </span>
          <span className="sb-spacer" />
          <button type="button" className="sb-btn sb-btn--ghost" onClick={() => setView("home")}>
            Home
          </button>
          {role === "Restaurant" && (
            <button type="button" className="sb-btn sb-btn--ghost" onClick={() => setView("create")}>
              Create
            </button>
          )}
          <button type="button" className="sb-btn sb-btn--ghost" onClick={() => setView("notifications")}>
            Notifications
          </button>
          {isProfileRole && (
            <button type="button" className="sb-btn sb-btn--ghost" onClick={() => setView("profile")}>
              Profile
            </button>
          )}
          <button type="button" className="sb-btn sb-btn--secondary" onClick={() => void api.signOutUser()}>
            Sign out
          </button>
        </div>
      </header>

      {view === "notifications" ? (
        <NotificationCenter uid={session.uid} />
      ) : view === "profile" && isProfileRole ? (
        <ProfileView
          profileUid={session.uid}
          profileName={displayName}
          role={role as "Restaurant" | "NGO"}
          viewerUid={session.uid}
        />
      ) : view === "create" && role === "Restaurant" ? (
        <DonationEditor uid={session.uid} onPublished={() => setView("home")} onCancel={() => setView("home")} />
      ) : role === "Restaurant" ? (
        <RestaurantDashboard uid={session.uid} onCreateDonation={() => setView("create")} />
      ) : role === "NGO" ? (
        <NGODashboard uid={session.uid} />
      ) : (
        <AdminPanel session={session} />
      )}
    </div>
  );
}
